import previewImage from "../../../assets/blog/maybeIAmCrazy.png"
import { Post } from "../../../types/postTypes"

export const whyIBuiltTech: Post = {
  metadata: {
    id: 23,
    title:
      "Why I Built Tech Without Being a Techie (And Why That's My Superpower)",
    author: "Zoee",
    date: "June 19, 2025",
    urlSlug: "why-i-built-tech-without-being-a-techie",
    description:
      "A deeply personal story of how Zoee’s founder built a coaching platform without a tech background—turning her outsider perspective into the very superpower that sets it apart.",
    previewImg: previewImage,
    tags: ["Coach", "Executive", "Leadership", "Career"],
  },
  content: [
    {
      type: "paragraph",
      text: "After 15 years in this space, I've learned something important: the coaching industry doesn't need more polish, perfection, or performance theater. What we really need are coaches who show up messy, honest, and completely themselves.",
    },
    {
      type: "paragraph",
      text: "And honestly? The world definitely doesn't need another tech platform built by people who've never walked in a coach's shoes.",
    },
    {
      type: "header2",
      text: "The Decision That Kept Me Up at Night",
    },
    {
      type: "paragraph",
      text: "Five years ago, I made a decision that terrified me: I decided to build a technology platform for coaches. The problem? I had zero tech experience. None. I couldn't code my way out of a paper bag, and I was about to compete against companies with entire engineering teams and venture capital backing.",
    },
    {
      type: "rich-text",
      items: [
        { type: "normal", text: "Everyone thought I was crazy.  " },
        { type: "italic", text: "Maybe I was." },
      ],
    },
    {
      type: "paragraph",
      text: "But when the imposter syndrome got so loud I could barely hear myself think, I kept coming back to this truth: I wasn't building Zoee because I understood code. I was building it because I understood coaches.",
    },
    {
      type: "header2",
      text: "When Outsiders Build Our Tools",
    },
    {
      type: "paragraph",
      text: "Here's what breaks my heart. Every single day, I watch coaches wrestling with platforms built by people who think they know what we need. These solutions are complicated, disconnected from our reality, and completely missing what coaching is actually about.",
    },
    {
      type: "paragraph",
      text: "Why does this keep happening? Because most of these tools were created by people who've never experienced that sacred moment when a client has a breakthrough. They've never felt the responsibility that comes with holding space for someone's transformation. To them, coaching is just another market to capture, another business vertical to optimize.",
    },
    {
      type: "paragraph",
      text: "That's not just my competition—that's the whole problem with how our industry approaches technology.",
    },
    {
      type: "header2",
      text: "My Faith Pulled Me Through",
    },
    {
      type: "paragraph",
      text: "When the technical challenges felt impossible, when investors questioned my lack of engineering background, when the imposter syndrome was deafening, my faith became my anchor. I believed — deeply — that I was called to solve this problem for coaches who were drowning in tech chaos.",
    },
    {
      type: "paragraph",
      text: "Every morning, I reminded myself: God doesn't call the equipped; He equips the called.",
    },
    {
      type: "paragraph",
      text: "My lack of technical expertise wasn't a bug—it was a feature. It meant I approached every problem from the coach's perspective, not the engineer's. It meant I prioritized simplicity over sophistication, impact over impressiveness.",
    },
    {
      type: "header2",
      text: "My Team and Community Inspired me Every Day",
    },
    {
      type: "paragraph",
      text: "The most beautiful part of this journey? I wasn't building alone. My incredible team spans time zones and continents, but we're united by something more powerful than geography: a shared mission to serve coaches better.",
    },
    {
      type: "paragraph",
      text: 'And the coaches themselves? They became my daily inspiration. Every conversation revealed another pain point. Every "I wish there was a tool that..." became a feature request. Every frustrated email about juggling twelve different platforms became fuel for our simplicity mission.',
    },
    {
      type: "paragraph",
      text: "They didn't need me to be a tech genius. They needed me to be someone who understood their world, their struggles, their dreams.",
    },
    {
      type: "header2",
      text: "The Simplicity Superpower",
    },
    {
      type: "paragraph",
      text: 'Here\'s what I learned building Zoee without a traditional tech background: When you don\t know all the "sophisticated" ways to solve a problem, you naturally gravitate toward the simplest solution.',
    },
    {
      type: "paragraph",
      text: "My competition build features because they can. I build features because coaches need them.",
    },
    {
      type: "paragraph",
      text: "My competition adds complexity because it looks impressive. I remove complexity because coaches are drowning in it.",
    },
    {
      type: "paragraph",
      text: "My competition solves problems from a technical perspective. I solve problems from a human perspective.",
    },
    {
      type: "header2",
      text: "Your Invitation to Get Real",
    },
    {
      type: "paragraph",
      text: "The coaching world is full of noise about getting rich quick, going viral, and building empires. But here's what I know to be true: The world needs coaches who show up authentically, build sustainable practices, and focus on the lives they're changing.",
    },
    {
      type: "paragraph",
      text: "You don't need to be perfect. You don't need to have all the answers. You don't need to fit someone else's mold.",
    },
    {
      type: "paragraph",
      text: "You just need to be you — and have systems simple enough to let your authentic gifts shine through.",
    },
    {
      type: "paragraph",
      text: "That's why I built Zoee. Not because I'm a tech expert, but because I'm a coach expert. Not because I understand algorithms, but because I understand the human heart.",
    },
    {
      type: "paragraph",
      text: "And sometimes, that's exactly the expertise the world needs.",
    },
    {
      type: "rich-text",
      items: [
        {
          type: "italic",
          text: "Nichole Lowe is the Founder & CEO of Zoee, the all-in-one coaching platform built by coaches, for coaches—designed to simplify your systems, support your growth, and scale your impact.",
        },
      ],
    },
  ],
}
